"use client";

import { useEffect, useState } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { resolveIconName } from "@portal/app/_common/lib/resolveIconName";
import { FA_ICON_VERSION } from "@portal/app/_generated/faIconVersion";

/**
 * Resolves an admin-configured icon name (stored as a runtime string in the
 * CMS) to a FontAwesome IconDefinition, fetched from our rolled-own icon CDN
 * under `public/fa-icons/`.
 *
 * Why this exists instead of `library.add(fas)` or a bundler dynamic import:
 *   - `library.add(fas)` bundles the full FA Pro Solid pack (~500–700 KB gz)
 *     into the client bundle — every picture-choice-using funnel pays that
 *     cost upfront
 *   - Bundler dynamic imports against node_modules template-literal paths
 *     don't work reliably under Turbopack
 *   - Fetching pre-built per-icon JSON from `/fa-icons/v{faVersion}/fa*.json`
 *     (generated by `scripts/buildFaIconCdn.ts`) keeps the client bundle
 *     lean and only transfers data for icons actually rendered
 *
 * The FA version is baked into the client bundle at build time via the
 * generated `FA_ICON_VERSION` constant, so rendering an icon is a single HTTP
 * round trip (no preceding manifest fetch).
 *
 * Cache layers:
 *   1. In-memory per-session cache keyed by module name (this file)
 *   2. Browser HTTP cache (versioned URL, immutable)
 *   3. CDN edge cache
 */

// Process-wide in-memory cache. Each entry is the Promise so concurrent renders
// for the same icon coalesce into a single network request.
const iconDefCache = new Map<string, Promise<IconDefinition | null>>();

/**
 * Converts any supported icon name format into the camelCase module name
 * used by the CDN (`faCow`, `faGraduationCap`).
 *
 * The Payload IconPicker stores camelCase names directly, so the
 * fast path is a pattern match. The fallback handles kebab and
 * `prefix:name` forms in case they ever appear.
 */
function toModuleName(icon: string | undefined): string | null {
  if (!icon) return null;
  const trimmed = icon.trim();
  if (!trimmed) return null;
  if (/^fa[A-Z0-9][A-Za-z0-9]*$/.test(trimmed)) return trimmed;
  const lookup = resolveIconName(trimmed);
  if (!lookup) return null;
  const pascal = lookup.iconName
    .split("-")
    .filter(Boolean)
    .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
    .join("");
  return pascal ? `fa${pascal}` : null;
}

function loadIcon(moduleName: string): Promise<IconDefinition | null> {
  const cached = iconDefCache.get(moduleName);
  if (cached) return cached;
  const promise = fetch(`/fa-icons/v${FA_ICON_VERSION}/${moduleName}.json`)
    .then((res) => (res.ok ? (res.json() as Promise<IconDefinition>) : null))
    .catch(() => null);
  iconDefCache.set(moduleName, promise);
  return promise;
}

interface UseFaIconResult {
  iconDef: IconDefinition | null;
  isLoading: boolean;
}

interface UseFaIconState extends UseFaIconResult {
  moduleName: string | null;
}

export function useFaIcon(icon: string | undefined): UseFaIconResult {
  const moduleName = toModuleName(icon);
  const [state, setState] = useState<UseFaIconState>({
    iconDef: null,
    isLoading: Boolean(moduleName),
    moduleName,
  });

  useEffect(() => {
    if (!moduleName) {
      setState({ iconDef: null, isLoading: false, moduleName: null });
      return;
    }

    setState({ iconDef: null, isLoading: true, moduleName });
    let cancelled = false;
    loadIcon(moduleName).then((result) => {
      if (!cancelled) setState({ iconDef: result, isLoading: false, moduleName });
    });
    return () => {
      cancelled = true;
    };
  }, [moduleName]);

  if (state.moduleName !== moduleName) {
    return { iconDef: null, isLoading: Boolean(moduleName) };
  }

  return { iconDef: state.iconDef, isLoading: state.isLoading };
}
